import { z } from 'zod';
import {
  commitmentStatus,
  direction,
  evidence,
  isoDate,
  span,
  uuid,
  closingMeeting,
  translationState,
} from './common';

/** One row of Open — a commitment, the meeting it came from, and who ended it. */
export const openItem = z.object({
  id: uuid,
  text: z.string(),
  ownerName: z.string().nullable(),
  ownerEmail: z.string().nullable(),
  grade: evidence,
  status: commitmentStatus,
  direction,
  /** The meeting it came from, with who was invited — the counterparty is one of them. */
  meeting: z.object({
    id: uuid,
    title: z.string(),
    startedAt: isoDate,
    attendees: z.array(z.string()),
  }),
  seriesKey: z.string().nullable(),
  span,
  postedToSlack: z.boolean(),
  createdAt: isoDate,
  /** When it is meant to be done by. Null: nobody said, which is the usual case. */
  dueAt: isoDate.nullable(),
  /** When somebody last confirmed it is still on. Null: never asked. */
  keptAt: isoDate.nullable(),
  /** The Asana task it became, when somebody converted it (PRD 51). */
  asanaTaskUrl: z.string().nullable(),
  closedAt: isoDate.nullable(),
  /**
   * Named, always: nothing just disappears (board 25). Null while open, and
   * null when a later meeting — not a person — closed it.
   */
  closedBy: z.object({ name: z.string().nullable(), email: z.string() }).nullable(),
  /** Or the meeting that closed it, when the product did the closing. */
  closedByMeeting: closingMeeting.nullable(),
  /** A hand-over's successor says where it came from. */
  handedOverFrom: z.object({ name: z.string().nullable(), email: z.string() }).nullable(),
});
export type OpenItem = z.infer<typeof openItem>;

export const openLedger = z.object({
  /** Open, newest first. The client groups by `direction`; `ownerEmail === null` is "needs an owner". */
  items: z.array(openItem),
  /**
   * The newest ends of the last seven days, newest first, with their closers —
   * at most `CLOSED_MOST`: a busy week is hundreds, and this list is for
   * glancing and undoing, not for reading (Vladimir, 2026-09-25: "Closed
   * recently section becomes very big … might slow the app after scaling").
   */
  closedThisWeek: z.array(openItem),
  counts: z.object({
    open: z.number().int(),
    needOwner: z.number().int(),
    overWeek: z.number().int(),
    /** Every end of the last seven days — `closedThisWeek` may hold fewer. */
    closed: z.number().int().default(0),
  }),
});
export type OpenLedger = z.infer<typeof openLedger>;

export const openQuery = z.object({
  /** Only what came out of meetings this person was in — the person page's filter. */
  person: z.string().email().optional(),
  series: z.string().min(1).max(200).optional(),
  /** Only what came out of the meetings a folder holds. */
  folder: uuid.optional(),
});
export type OpenQuery = z.infer<typeof openQuery>;

/** Done and dropped end it here. Handing over is `assignOwnerBody`; superseding is a later meeting's doing. */
export const closeCommitmentBody = z.object({ status: z.enum(['DONE', 'DROPPED']) });
export type CloseCommitmentBody = z.infer<typeof closeCommitmentBody>;

/**
 * An action somebody writes down themselves, on the meeting's page.
 *
 * Vladimir, 2026-09-23: *"add possibility to create actions in the meeting
 * page so that I can add something myself, assign owner, deadline etc and
 * then post to slack with my created as well"*. The owner and the deadline
 * are optional for the same reason they are on an extracted one: a promise
 * with nobody's name on it yet is still a promise.
 */
export const createCommitmentBody = z.object({
  text: z.string().trim().min(3).max(500),
  ownerEmail: z.string().email().max(320).optional(),
  ownerName: z.string().trim().min(1).max(120).optional(),
  dueAt: isoDate.nullable().optional(),
});
export type CreateCommitmentBody = z.infer<typeof createCommitmentBody>;

export const assignOwnerBody = z.object({
  email: z.string().email().max(320),
  name: z.string().trim().min(1).max(120).optional(),
});
export type AssignOwnerBody = z.infer<typeof assignOwnerBody>;

/**
 * One commitment, with everything a person needs to remember it: the lines
 * it was said in, the meeting around it, what else that meeting produced,
 * the chain of hand-overs, what the same person still owes in the series,
 * and where it stands. A dry line of text is not a memory; this is.
 */
export const commitmentLine = z.object({
  id: uuid,
  speakerName: z.string().nullable(),
  text: z.string(),
  spokeAt: isoDate.nullable(),
  /** Inside the span the model marked — the lines the commitment was made in. */
  inSpan: z.boolean(),
});

export const commitmentDetail = z.object({
  item: openItem,
  meeting: z.object({
    id: uuid,
    title: z.string(),
    startedAt: isoDate,
    endedAt: isoDate.nullable(),
    attendees: z.array(z.object({ name: z.string().nullable(), email: z.string() })),
    seriesKey: z.string().nullable(),
    seriesTitle: z.string().nullable(),
    /** The note's first paragraphs — what the meeting was about. */
    summary: z.array(z.string()),
    video: z.boolean(),
  }),
  /** Three lines before the span, the span, three after — for readers of the transcript; else empty. */
  lines: z.array(commitmentLine),
  canReadTranscript: z.boolean(),
  siblings: z.object({
    commitments: z.array(
      z.object({
        id: uuid,
        text: z.string(),
        ownerName: z.string().nullable(),
        ownerEmail: z.string().nullable(),
        status: openItem.shape.status,
      }),
    ),
    decisions: z.array(
      z.object({ id: uuid, text: z.string(), status: z.enum(['STANDS', 'SUPERSEDED', 'RETIRED']) }),
    ),
  }),
  /** Hand-overs, oldest first, this one included. One link when it was never handed over. */
  chain: z.array(
    z.object({
      id: uuid,
      text: z.string(),
      ownerName: z.string().nullable(),
      ownerEmail: z.string().nullable(),
      status: openItem.shape.status,
      createdAt: isoDate,
      closedAt: isoDate.nullable(),
      current: z.boolean(),
    }),
  ),
  /** Still open in the same series for the same owner. */
  related: z.array(openItem),
  /** Where it stands, as moments: heard, handed over, marked done, dropped, closed by what was said. */
  history: z.array(
    z.object({
      at: isoDate,
      what: z.string(),
      who: z.object({ name: z.string().nullable(), email: z.string() }).nullable(),
      meeting: z.object({ id: uuid, title: z.string() }).nullable(),
    }),
  ),
  access: z.object({ canEdit: z.boolean() }),
  /** Shown in the reader's language where that is not the one it was written in (PRD 57). */
  translation: translationState.nullable(),
});
export type CommitmentDetail = z.infer<typeof commitmentDetail>;

export const updateCommitmentBody = z
  .object({
    text: z.string().trim().min(3).max(500).optional(),
    /** "Yes, still on" — pushes it out of the stale band without closing it. */
    kept: z.literal(true).optional(),
    /** A deadline, or null to take one off. */
    dueAt: isoDate.nullable().optional(),
  })
  .refine((b) => b.text !== undefined || b.kept !== undefined || b.dueAt !== undefined, {
    message: 'nothing to change',
  });
export type UpdateCommitmentBody = z.infer<typeof updateCommitmentBody>;
